import { useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CampaignStatus, Page, ParticipationCampaign } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';
import { CampaignDetail } from './CampaignDetail';

const STATUS_OPTIONS: CampaignStatus[] = ['DRAFT', 'OPEN', 'CLOSED', 'FINALIZED', 'CANCELLED'];

/** tomorrow / +30 days, local wall-clock, as datetime-local input defaults. */
function defaultWindow(): { opensAt: string; closesAt: string } {
  const toLocal = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const opens = new Date();
  opens.setDate(opens.getDate() + 1);
  opens.setHours(0, 0, 0, 0);
  const closes = new Date(opens);
  closes.setDate(closes.getDate() + 30);
  return { opensAt: toLocal(opens), closesAt: toLocal(closes) };
}

/**
 * Participatory-budgeting campaign management — GET /campaigns?district=
 * (public endpoint, always scoped to the persona's own district here),
 * create (admin), and open/close/finalize/cancel lifecycle actions in
 * CampaignDetail.tsx.
 */
export function CampaignsConsole() {
  const { persona } = useAuth();
  const district = persona?.district ?? '';
  const isAdmin = persona?.roles.includes('DISTRICT_ADMINISTRATOR') ?? false;
  const [status, setStatus] = useState<CampaignStatus | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const qs = new URLSearchParams({ limit: '100' });
  if (district) qs.set('district', district);
  if (status) qs.set('status', status);
  const campaignsQ = useApiQuery<Page<ParticipationCampaign>>(district ? `/api/v1/m41/campaigns?${qs.toString()}` : null);
  const campaigns = campaignsQ.data?.items ?? [];

  if (!district) return <ErrorNote message="This account has no district binding on file." />;

  return (
    <div>
      <PageHeader
        title="Participatory Budget"
        subtitle="Open and close voting rounds, then finalize vote-ranked funding for this district."
        actions={
          isAdmin ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New campaign
            </button>
          ) : undefined
        }
      />

      <div className="mb-4">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as CampaignStatus | '')}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {campaignsQ.error ? (
        <div className="mb-4">
          <ErrorNote message={campaignsQ.error} />
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        {campaignsQ.loading ? (
          <Spinner />
        ) : campaigns.length === 0 ? (
          <EmptyState title="No campaigns match these filters" hint={isAdmin ? 'Create one to start a participatory-budgeting round.' : undefined} />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Title</th>
                <th className="table-head text-right">Budget envelope</th>
                <th className="table-head">Voting window</th>
                <th className="table-head text-right">Allocated</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs">{c.refNo}</td>
                  <td className="table-cell font-medium">{c.title}</td>
                  <td className="table-cell text-right tabular-nums">{scr(c.budgetEnvelopeScr)}</td>
                  <td className="table-cell text-xs">
                    {formatDateTime(c.opensAt)} → {formatDateTime(c.closesAt)}
                  </td>
                  <td className="table-cell text-right tabular-nums">{c.totalAllocatedScr != null ? scr(c.totalAllocatedScr) : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <CampaignDetail
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => campaignsQ.reload()}
        />
      ) : null}
      {showCreate ? (
        <CreateCampaignModal
          district={district}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            campaignsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateCampaignModal({ district, onClose, onDone }: { district: string; onClose: () => void; onDone: () => void }) {
  const win = defaultWindow();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [budgetEnvelopeScr, setBudgetEnvelopeScr] = useState('');
  const [opensAt, setOpensAt] = useState(win.opensAt);
  const [closesAt, setClosesAt] = useState(win.closesAt);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m41/campaigns', {
        district,
        title: title.trim(),
        description: description.trim() || undefined,
        budgetEnvelopeScr: Number.parseFloat(budgetEnvelopeScr || '0'),
        opensAt: new Date(opensAt).toISOString(),
        closesAt: new Date(closesAt).toISOString(),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create campaign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New participatory-budget campaign" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 2027 Community Budget" />
        </Field>
        <Field label="Description (optional)">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Budget envelope (SCR)">
          <input className="field-input" type="number" min="1" step="0.01" value={budgetEnvelopeScr} onChange={(e) => setBudgetEnvelopeScr(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Voting opens">
            <input className="field-input" type="datetime-local" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} />
          </Field>
          <Field label="Voting closes">
            <input className="field-input" type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || title.trim().length < 2 || Number.parseFloat(budgetEnvelopeScr || '0') <= 0 || Date.parse(closesAt) <= Date.parse(opensAt)}
          >
            {busy ? 'Creating…' : 'Create campaign'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
